/**
 * Bulk Import Service
 *
 * Handles API calls for bulk import operations including:
 * - File preview with column detection
 * - Starting import jobs
 * - Tracking import progress
 */

import axiosClient from '../lib/http/axiosClient';

class BulkImportService {
  /**
   * Preview import file and get column suggestions
   * @param {string} tableId - Table ID to import into
   * @param {File} file - File to preview
   * @returns {Promise<Object>} Preview data with suggestions
   */
  async previewImport(tableId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosClient.post(
      `/master-data/tables/${tableId}/import/preview`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  /**
   * Start bulk import job
   * @param {string} tableId - Table ID to import into
   * @param {File} file - File to import
   * @param {Object} columnMapping - Column mapping object
   * @param {Object} options - Import options
   * @returns {Promise<Object>} Import job details
   */
  async startImport(tableId, file, columnMapping, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('column_mapping', JSON.stringify(columnMapping));
    formData.append('skip_header', options.skipHeader ?? true);
    formData.append('ignore_errors', options.ignoreErrors ?? false);

    const response = await axiosClient.post(
      `/master-data/tables/${tableId}/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  /**
   * Get import job status
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Job status and progress
   */
  async getJobStatus(jobId) {
    const response = await axiosClient.get(
      `/master-data/import/jobs/${jobId}`
    );

    return response.data;
  }

  /**
   * Download import template for a table
   * @param {string} tableId - Table ID
   * @returns {Promise<Blob>} Template file
   */
  async downloadTemplate(tableId) {
    const response = await axiosClient.get(
      `/master-data/tables/${tableId}/template`,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  }
}

export const bulkImportService = new BulkImportService();
export default bulkImportService;
